// The one place the ambient browser timezone is read.
//
// Kept out of `sessionTracks.js` on purpose: that module stays a pure, zone-as-
// argument derivation, so its tests never depend on the machine's TZ. Anything
// that needs "where is the person looking at this screen" comes through here.

/** Fallback when the runtime cannot resolve a zone at all. */
const FALLBACK_ZONE = "UTC";

/**
 * The viewer's IANA timezone, e.g. "Asia/Kolkata".
 *
 * @returns {string} Always a usable zone id; falls back to "UTC".
 */
export function viewerTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_ZONE;
  } catch {
    return FALLBACK_ZONE;
  }
}

/**
 * Whether a string is a zone this runtime can actually format in. Guards the
 * coach-timezone hook against a bad value from the API taking the panel down.
 *
 * @param {string|null|undefined} timeZone
 * @returns {boolean}
 */
export function isValidTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== "string") return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Short label for a zone at a given instant — "PDT", "GMT+5:30".
 *
 * The exact string is locale-dependent (an en-US runtime renders Asia/Kolkata as
 * "GMT+5:30", not "IST"), and it is offset-dependent, which is why it takes a
 * date: the same zone reads PST in January and PDT in July.
 *
 * @param {Date} date - Instant to resolve the offset at.
 * @param {string} timeZone - IANA zone id.
 * @returns {string} Empty string when the zone cannot be formatted.
 */
export function zoneAbbreviation(date, timeZone) {
  if (!isValidTimeZone(timeZone)) return "";
  try {
    const part = new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: "short",
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName");
    return part ? part.value : "";
  } catch {
    return "";
  }
}
